import api from './api';

/**
 * 站点监控服务类 - 处理监控站点相关API请求
 */
class SiteService {
  /**
   * 获取监控站点列表
   * @param {Object} params - 查询参数
   * @param {number} params.page - 页码
   * @param {number} params.limit - 每页数量
   * @param {string} params.search - 搜索关键词 (可选)
   * @param {string} params.sortBy - 排序字段 (可选)
   * @param {string} params.sortOrder - 排序方向 (可选)
   * @returns {Promise} 包含站点列表的Promise
   */
  async getSites(params = {}) {
    try {
      const response = await api.get('/sites', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 获取单个监控站点详情
   * @param {string} siteId - 站点ID
   * @returns {Promise} 包含站点详情的Promise
   */
  async getSite(siteId) {
    try {
      const response = await api.get(`/sites/${siteId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 创建新的监控站点
   * @param {Object} siteData - 站点数据
   * @param {string} siteData.name - 站点名称
   * @param {string} siteData.url - 站点URL
   * @param {string} siteData.frequency - 监控频率
   * @param {boolean} siteData.isActive - 是否激活监控
   * @param {Array} siteData.pages - 要监控的页面列表 (可选)
   * @returns {Promise} 包含创建的站点详情的Promise
   */
  async createSite(siteData) {
    try {
      const response = await api.post('/sites', siteData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 更新监控站点
   * @param {string} siteId - 站点ID
   * @param {Object} siteData - 站点更新数据
   * @returns {Promise} 包含更新后的站点详情的Promise
   */
  async updateSite(siteId, siteData) {
    try {
      const response = await api.put(`/sites/${siteId}`, siteData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 删除监控站点
   * @param {string} siteId - 站点ID
   * @returns {Promise} 包含操作结果的Promise
   */
  async deleteSite(siteId) {
    try {
      const response = await api.delete(`/sites/${siteId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 添加监控页面
   * @param {string} siteId - 站点ID
   * @param {Object} pageData - 页面数据
   * @param {string} pageData.url - 页面URL
   * @param {string} pageData.name - 页面名称 (可选)
   * @param {string} pageData.selector - CSS选择器 (可选)
   * @returns {Promise} 包含添加的页面详情的Promise
   */
  async addPage(siteId, pageData) {
    try {
      const response = await api.post(`/sites/${siteId}/pages`, pageData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 更新监控页面
   * @param {string} siteId - 站点ID
   * @param {string} pageId - 页面ID
   * @param {Object} pageData - 页面更新数据
   * @returns {Promise} 包含更新后的页面详情的Promise
   */
  async updatePage(siteId, pageId, pageData) {
    try {
      const response = await api.put(`/sites/${siteId}/pages/${pageId}`, pageData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 删除监控页面
   * @param {string} siteId - 站点ID
   * @param {string} pageId - 页面ID
   * @returns {Promise} 包含操作结果的Promise
   */
  async deletePage(siteId, pageId) {
    try {
      const response = await api.delete(`/sites/${siteId}/pages/${pageId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 手动检查站点变更
   * @param {string} siteId - 站点ID
   * @returns {Promise} 包含检查结果的Promise
   */
  async checkSite(siteId) {
    try {
      const response = await api.post(`/sites/${siteId}/check`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 获取站点监控状态历史
   * @param {string} siteId - 站点ID
   * @param {Object} params - 查询参数
   * @param {string} params.startDate - 开始日期 (可选)
   * @param {string} params.endDate - 结束日期 (可选)
   * @param {number} params.limit - 返回的记录数量 (可选)
   * @returns {Promise} 包含监控历史的Promise
   */
  async getSiteHistory(siteId, params = {}) {
    try {
      const response = await api.get(`/sites/${siteId}/history`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 启用站点监控
   * @param {string} siteId - 站点ID
   * @returns {Promise} 包含操作结果的Promise
   */
  async enableSite(siteId) {
    try {
      const response = await api.patch(`/sites/${siteId}/enable`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 禁用站点监控
   * @param {string} siteId - 站点ID
   * @returns {Promise} 包含操作结果的Promise
   */
  async disableSite(siteId) {
    try {
      const response = await api.patch(`/sites/${siteId}/disable`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 获取站点统计信息
   * @returns {Promise} 包含站点统计信息的Promise
   */
  async getSiteStats() {
    try {
      const response = await api.get('/sites/stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 批量删除站点
   * @param {Array} siteIds - 站点ID数组
   * @returns {Promise} 包含操作结果的Promise
   */
  async deleteMultipleSites(siteIds) {
    try {
      const response = await api.delete('/sites/batch', { data: { siteIds } });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 批量添加游戏站点
   * @param {Array} sitesData - 站点数据数组
   * @returns {Promise} 包含操作结果的Promise
   */
  async addGameSites(sitesData) {
    try {
      const response = await api.post('/sites/games/batch', { sites: sitesData });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 获取/检测站点地图
   * @param {string} siteId - 站点ID
   * @returns {Promise} 包含站点地图的Promise
   */
  async detectSitemap(siteId) {
    try {
      const response = await api.post(`/sites/${siteId}/detect-sitemap`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 批量导入站点
   * @param {Array} sites - 站点数据数组
   * @returns {Promise} 包含操作结果的Promise
   */
  async batchImportSites(sites) {
    try {
      const response = await api.post('/sites/batch/import', { sites });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * 处理API错误
   * @param {Error} error - API错误对象
   * @returns {Error} 格式化后的错误对象
   */
  handleError(error) {
    if (error.response) {
      // 服务器响应状态码超出 2xx 范围
      const errorMsg = error.response.data.message || '操作失败，请稍后再试';
      const customError = new Error(errorMsg);
      customError.statusCode = error.response.status;
      return customError;
    } else if (error.request) {
      // 请求已发送但没有收到响应
      return new Error('无法连接到服务器，请检查您的网络连接');
    } else {
      // 请求设置时发生错误
      return new Error('发送请求时出错，请稍后再试');
    }
  }

  /**
   * 模拟获取站点列表（开发环境使用）
   * @returns {Object} 模拟的站点列表数据
   */
  mockGetSites() {
    const mockData = {
      sites: [
        {
          id: '1',
          name: 'Example Website',
          url: 'https://example.com',
          frequency: 'daily',
          isActive: true,
          lastChecked: new Date(Date.now() - 3600000).toISOString(),
          totalPages: 3,
          createdAt: new Date(Date.now() - 2592000000).toISOString(),
          status: 'normal'
        },
        {
          id: '2',
          name: 'Test Organization',
          url: 'https://test.org',
          frequency: 'hourly',
          isActive: true,
          lastChecked: new Date(Date.now() - 1800000).toISOString(),
          totalPages: 1,
          createdAt: new Date(Date.now() - 1728000000).toISOString(),
          status: 'normal'
        },
        {
          id: '3',
          name: 'Sample Portal',
          url: 'https://sample.net',
          frequency: 'weekly',
          isActive: false,
          lastChecked: new Date(Date.now() - 604800000).toISOString(),
          totalPages: 5,
          createdAt: new Date(Date.now() - 864000000).toISOString(),
          status: 'error'
        }
      ],
      pagination: {
        page: 1,
        limit: 10,
        totalItems: 3,
        totalPages: 1
      }
    };
    
    return mockData;
  }

  /**
   * 模拟获取站点详情（开发环境使用）
   * @param {string} siteId - 站点ID
   * @returns {Object} 模拟的站点详情数据
   */
  mockGetSite(siteId) {
    return {
      id: siteId,
      name: 'Example Website',
      url: 'https://example.com',
      frequency: 'daily',
      isActive: true,
      lastChecked: new Date(Date.now() - 3600000).toISOString(),
      createdAt: new Date(Date.now() - 2592000000).toISOString(),
      status: 'normal',
      notifyBy: ['email', 'app'],
      pages: [
        {
          id: '1',
          url: 'https://example.com',
          name: '首页',
          selector: 'main',
          lastChanged: new Date(Date.now() - 86400000).toISOString(),
          status: 'normal'
        },
        {
          id: '2',
          url: 'https://example.com/about',
          name: '关于我们',
          selector: '.content',
          lastChanged: new Date(Date.now() - 172800000).toISOString(),
          status: 'normal'
        },
        {
          id: '3',
          url: 'https://example.com/contact',
          name: '联系方式',
          selector: '#contact-info',
          lastChanged: null,
          status: 'pending'
        }
      ]
    };
  }
}

const siteService = new SiteService();
export default siteService; 